import type { LeagueContext } from '@api-service/league-context';

/**
 * The gameweek whose deadline closes the Mini-League Promise change window.
 * Originally gameweek 1; extended to gameweek 3 (FFH-2672) after support
 * tickets from members whose league outgrew eligibility or who were blocked
 * by the account page issues around the original cut-off (FFH-2623,
 * FFH-2635).
 */
const CHANGE_WINDOW_FINAL_GAMEWEEK = 3;

/**
 * The 2026/27 gameweek 3 deadline, used while the league context is
 * unavailable or has not yet reached gameweek 3 (it only exposes the current
 * and next gameweeks). The context's own deadlineAt wins once gameweek 3 is
 * visible.
 */
export const CHANGE_WINDOW_DEADLINE_FALLBACK = '2026-09-04T17:30:00Z';

export const DEADLINE_DISPLAY_FORMAT = 'dddd D MMMM, h:mma';

/**
 * When the Mini-League Promise change window closes: the gameweek 3 deadline,
 * after which a stored nomination can no longer be changed (the offer terms'
 * change cut-off). Null means the deadline has passed and cannot be recovered
 * from the context: the season is beyond gameweek 3.
 */
export const resolveChangeDeadlineAt = (
  context: LeagueContext | null
): string | null => {
  if (!context) return CHANGE_WINDOW_DEADLINE_FALLBACK;
  if (context.currentGameweek?.number === CHANGE_WINDOW_FINAL_GAMEWEEK) {
    return context.currentGameweek.deadlineAt;
  }
  if (context.nextGameweek?.number === CHANGE_WINDOW_FINAL_GAMEWEEK) {
    return context.nextGameweek.deadlineAt;
  }
  // Earlier gameweeks: the window is still open, but the closing gameweek's
  // own deadline is not in the context yet, so the fallback stands in.
  const currentNumber = context.currentGameweek?.number ?? null;
  const nextNumber = context.nextGameweek?.number ?? null;
  const isBeforeClosingGameweek =
    (currentNumber !== null && currentNumber < CHANGE_WINDOW_FINAL_GAMEWEEK) ||
    (nextNumber !== null && nextNumber < CHANGE_WINDOW_FINAL_GAMEWEEK);
  if (isBeforeClosingGameweek) return CHANGE_WINDOW_DEADLINE_FALLBACK;
  // Early pre-season can have no gameweeks at all; the fixture list simply
  // has not been published yet, so the change window is still open.
  if (context.seasonStatus === 'PRE_SEASON') {
    return CHANGE_WINDOW_DEADLINE_FALLBACK;
  }
  return null;
};
